// =============================================================================
// Project Management module — portfolios list
//
// Standard ModelListPage over the portfolio model class. Deletion is bespoke: the confirm dialog
// makes the operator decide the fate of the portfolio's projects — cascade-delete them, or keep
// them and just remove them from the portfolio.
// =============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { ModelListPage, ModelListPageStyled } from '/imports/ui/ModelListPage';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne } from '/imports/hooks';
import { Accounts } from '/imports/api/accounts';
import {
  ProjectManagementPortfolioRow, projectManagementIsAdmin,
  projectManagementPortfolioModelClass, projectManagementDeletePortfolio,
} from '/imports/api/projectManagement';

const PortfoliosListStyled = styled(ModelListPageStyled)`
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: 0.85rem; }
`;

const DeleteModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.5rem 0; }
  .modal-buttons { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.25rem; flex-wrap: wrap; }
`;

/** Portfolio catalog with the two-choice delete (cascade projects vs keep them un-portfolio'd). */
export const ProjectManagementPortfoliosPage = () => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const [deleting, setDeleting] = useState<ProjectManagementPortfolioRow>();
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  if (!projectManagementIsAdmin(user, account)) return <NotPermittedPage />;  // portfolios are org-wide grouping — admin-only

  const doDelete = (mode: 'deleteProjects' | 'keepProjects') => toastOnError(async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      const res = await projectManagementDeletePortfolio.call({ id: deleting._id, mode });
      toast.info(`${deleting.name} · ${strings.Projects()}: ${res.projects}`);
      setDeleting(undefined);
      setReloadKey((k) => k + 1);  // remount the catalog so the deleted row disappears
    }
    finally {
      setBusy(false);
    }
  })();

  return (
    <>
      <ModelListPage
        key={reloadKey}
        modelClass={projectManagementPortfolioModelClass}
        styled={PortfoliosListStyled}
        defaultSort={{ name: 1 }}
        columns={[
          { field: 'name', render: (doc) => <Link to={`/project-management/portfolios/${doc._id}`}>{doc.name}</Link> },
          { field: 'note', label: strings.Note },
          { field: '_id', label: strings.Projects, render: (doc) => `${(doc as ProjectManagementPortfolioRow).projectCount ?? 0}` },
          {
            field: 'active',
            label: '',
            render: (doc) => <button className="link-btn" onClick={() => setDeleting(doc as ProjectManagementPortfolioRow)}>{strings.Delete()}</button>,
          },
        ]}
      />
      {
        deleting &&
          <Modal styled={DeleteModalStyled} title={`${strings.Delete()} "${deleting.name}"`} onClose={() => setDeleting(undefined)}>
            <div className="meta-row">{strings.Projects()}: {deleting.projectCount ?? 0}</div>
            <div className="meta-row">{strings.PortfolioDeleteExplainer()}</div>
            <div className="modal-buttons">
              <Button className="secondary" onClick={() => setDeleting(undefined)}>{strings.Cancel()}</Button>
              <Button className="secondary" disabled={busy} onClick={() => doDelete('deleteProjects')}>{strings.DeletePortfolioAndProjects()}</Button>
              <Button className="primary" disabled={busy} onClick={() => doDelete('keepProjects')}>{strings.DeletePortfolioOnly()}</Button>
            </div>
          </Modal>
      }
    </>
  );
};
